import type { MortalityLog } from '../types/production'

import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { enqueueEntityChange, enqueueEntityChanges } from '../offline/outbox/entityOutbox'
import { getOfflineDb } from '../offline/dbFactory'
import { dexieMortalityLogRepo, type MortalityLogListParams } from '../offline/repos/DexieMortalityLogRepo'
import { normalizeToIsoZ } from './dateNormalization'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'
import { createId } from './id'
import { api } from './api'

const nowIso = (): string => new Date().toISOString()

const toOptionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const normalizeMortalityCount = (value: unknown): string => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
      throw new Error('Mortality count must be a non-negative whole number')
    }
    return value.toString()
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      throw new Error('Mortality count is required')
    }

    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
      throw new Error('Mortality count must be a non-negative whole number')
    }

    return parsed.toString()
  }

  throw new Error('Mortality count is required')
}

const normalizeRecordedAt = (raw?: string): string => {
  if (!raw) return nowIso()
  const normalized = normalizeToIsoZ(raw)
  if (normalized.ok) return normalized.isoZ
  return nowIso()
}

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const REMOTE_LIST_TTL_MS = 30_000
const lastRemoteListAtByKey = new Map<string, number>()

const buildRemoteListKey = (params: MortalityLogListParams): string => {
  const clean = {
    tenantKey: getActiveTenantKey(),
    farmId: params.farmId?.trim() || '',
    productionUnitId: params.productionUnitId?.trim() || '',
    lot: params.lot?.trim() || '',
    lotId: params.lotId?.trim() || '',
    fromRecordedAt: params.fromRecordedAt?.trim() || '',
    toRecordedAt: params.toRecordedAt?.trim() || ''
  }
  return JSON.stringify(clean)
}

const shouldAttemptRemoteList = (params: MortalityLogListParams): boolean => {
  if (!isOnline()) return false
  const key = buildRemoteListKey(params)
  const lastAt = lastRemoteListAtByKey.get(key)
  if (!lastAt) return true
  return Date.now() - lastAt > REMOTE_LIST_TTL_MS
}

const getPendingMortalityLogIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(
    entries
      .filter((entry) => entry.entityType === 'mortalityLog')
      .map((entry) => entry.entityId)
  )
}

const hasPendingMortalityLogOutboxEntry = async (entityId: string): Promise<boolean> => {
  const trimmed = entityId.trim()
  if (!trimmed) return false

  try {
    const db = getOfflineDb()
    const match = await db.entityOutbox.where('[entityType+entityId]').equals(['mortalityLog', trimmed]).first()
    return Boolean(match)
  } catch {
    return false
  }
}

const pruneRemovedRemoteMortalityLogs = async (cached: MortalityLog[], remoteMapped: MortalityLog[]): Promise<void> => {
  const remoteIds = new Set(remoteMapped.map((log) => log.id))

  for (const log of cached) {
    if (remoteIds.has(log.id)) continue
    // Preserve local-only changes until their outbox entry is flushed.
    // eslint-disable-next-line no-await-in-loop
    const hasPending = await hasPendingMortalityLogOutboxEntry(log.id)
    if (hasPending) continue
    // eslint-disable-next-line no-await-in-loop
    await dexieMortalityLogRepo.delete(log.id)
  }
}

const normalizeRemoteMortalityLog = (candidate: any): MortalityLog | null => {
  if (!candidate || typeof candidate !== 'object') return null
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const productionUnitId = typeof candidate.productionUnitId === 'string' ? candidate.productionUnitId.trim() : ''
  const recordedAtRaw = typeof candidate.recordedAt === 'string' ? candidate.recordedAt.trim() : ''
  const mortalityCountRaw = candidate.mortalityCount
  if (!id || !farmId || !productionUnitId || !recordedAtRaw) return null

  try {
    return {
      id,
      farmId,
      productionUnitId,
      lot: typeof candidate.lot === 'string' ? candidate.lot.trim() || undefined : undefined,
      lotId: typeof candidate.lotId === 'string' ? candidate.lotId.trim() || undefined : undefined,
      capturedByActorId: typeof candidate.capturedByActorId === 'string' ? candidate.capturedByActorId.trim() || undefined : undefined,
      captureSource: toOptionalTrimmed(candidate.captureSource),
      recordedAt: normalizeRecordedAt(recordedAtRaw),
      mortalityCount: normalizeMortalityCount(mortalityCountRaw),
      notes: toOptionalTrimmed(candidate.notes),
      metadata: typeof candidate.metadata === 'object' && candidate.metadata !== null ? candidate.metadata : undefined,
      createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : undefined,
      updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : undefined
    }
  } catch {
    return null
  }
}

export type MortalityLogCreatePayload = {
  id?: string
  farmId: string
  productionUnitId: string
  lot?: string
  lotId?: string
  // Charter D-4: the Crew Mode capture screen stamps the active operator's
  // actorId here. Absent on the admin write path — the server then falls
  // back to the request principal (captureAttribution.ts) so no row is
  // anonymous.
  capturedByActorId?: string
  recordedAt?: string
  mortalityCount: string | number
  notes?: string
  metadata?: Record<string, unknown>
}

export type MortalityLogUpdatePayload = Partial<{
  lot: string | null
  lotId: string | null
  capturedByActorId: string
  recordedAt: string
  mortalityCount: string | number
  notes: string | null
  metadata: Record<string, unknown> | null
}>

export type MortalityLogMutationResult = EntityMutationResult<MortalityLog>
export type MortalityLogDeleteResult = EntityMutationResult<void>

export type MortalityLogWriteOptions = {
  // Server-Side Flag Enforcement v0 (docs/crew-mode/prd/crew-mode-server-side-
  // flag-enforcement-v0.md): set only by Crew Mode's capture screen. Never
  // persisted — carried only in the outbox payload for the server's
  // MortalityLogService.create guard to read. The admin app never sets this.
  source?: 'crewMode'
}

const normalizeMortalityLogRecord = (args: {
  candidate: MortalityLogCreatePayload | MortalityLog
  id?: string
  createdAt?: string
  updatedAt?: string
}): MortalityLog => {
  const farmId = toOptionalTrimmed(args.candidate.farmId)
  if (!farmId) throw new Error('Facility is required')

  const productionUnitId = toOptionalTrimmed(args.candidate.productionUnitId)
  if (!productionUnitId) throw new Error('Production unit is required')

  const timestamp = nowIso()
  const candidateId = typeof (args.candidate as MortalityLog).id === 'string'
    ? (args.candidate as MortalityLog).id.trim()
    : ''

  return {
    id: args.id?.trim() || candidateId || createId('mort'),
    farmId,
    productionUnitId,
    lot: toOptionalTrimmed(args.candidate.lot),
    lotId: toOptionalTrimmed(args.candidate.lotId),
    capturedByActorId: toOptionalTrimmed(args.candidate.capturedByActorId),
    captureSource: toOptionalTrimmed((args.candidate as { captureSource?: unknown }).captureSource),
    recordedAt: normalizeRecordedAt(args.candidate.recordedAt),
    mortalityCount: normalizeMortalityCount(args.candidate.mortalityCount),
    notes: toOptionalTrimmed(args.candidate.notes),
    metadata: args.candidate.metadata,
    createdAt: args.createdAt?.trim() || timestamp,
    updatedAt: args.updatedAt?.trim() || timestamp
  }
}

export const mortalityLogApi = {
  readMortalityLogSyncStatus: async (logIds: string[]): Promise<EntityBatchSyncStatus> => {
    return readEntityBatchSyncStatus('mortalityLog', logIds)
  },

  retryMortalityLogSyncStatusWithReport: async (logIds: string[]): Promise<EntityBatchSyncRetryReport> => {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'mortalityLog',
      entityIds: logIds
    })
  },

  list: async (
    params: MortalityLogListParams = {},
    options?: { preferLocal?: boolean; forceRemote?: boolean }
  ): Promise<MortalityLog[]> => {
    await ensureOfflineReady()

    const [cached, pendingIds] = await Promise.all([
      dexieMortalityLogRepo.list(params),
      getPendingMortalityLogIds()
    ])
    const shouldFetchRemote = options?.forceRemote ? isOnline() : shouldAttemptRemoteList(params)

    // v2.1 finding #56 → finding #57 (Phase 4 Session 2 Block A4): the #56 fix
    // narrowed the `preferLocal: true` short-circuit to `cached.length > 0`,
    // but that gate was scope-level, not freshness-aware. A teammate's laptop
    // that had ANY prior cache for the scope (older days, prior sessions)
    // still short-circuited and never saw new peer events. The TTL-based
    // `shouldAttemptRemoteList` already preserves the zero-network perf path
    // within `REMOTE_LIST_TTL_MS`, so the dedicated `preferLocal` short-circuit
    // is removed in favor of trusting that gate uniformly. `preferLocal`
    // remains in the call signature for back-compat — within the TTL window
    // the function still returns cached without a network round-trip; outside
    // it, the function awaits a remote fetch so peer events surface.
    if (!shouldFetchRemote) {
      return cached
    }

    const key = buildRemoteListKey(params)
    try {
      const remote = await api.mortalityLogs.list({
        farmId: params.farmId,
        productionUnitId: params.productionUnitId,
        lot: params.lot,
        lotId: params.lotId,
        fromRecordedAt: params.fromRecordedAt,
        toRecordedAt: params.toRecordedAt,
        noCache: options?.forceRemote
      })

      const mapped = remote
        .map(normalizeRemoteMortalityLog)
        .filter((log): log is MortalityLog => Boolean(log))
        .filter((log) => !pendingIds.has(log.id))

      await pruneRemovedRemoteMortalityLogs(cached, mapped)
      await dexieMortalityLogRepo.upsertMany(mapped)
      lastRemoteListAtByKey.set(key, Date.now())

      return dexieMortalityLogRepo.list(params)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[mortalityLogApi] remote list failed; falling back to cache', error)
      }
      if (cached.length > 0) return cached
      throw error
    }
  },

  get: async (id: string): Promise<MortalityLog | undefined> => {
    await ensureOfflineReady()
    return dexieMortalityLogRepo.getById(id)
  },

  create: async (
    payload: MortalityLogCreatePayload,
    options?: MortalityLogWriteOptions
  ): Promise<MortalityLogMutationResult> => {
    await ensureOfflineReady()
    const timestamp = nowIso()
    const log = normalizeMortalityLogRecord({
      candidate: payload,
      id: payload.id,
      createdAt: timestamp,
      updatedAt: timestamp
    })

    await dexieMortalityLogRepo.upsert(log)
    await enqueueEntityChange({
      entityType: 'mortalityLog',
      op: 'upsert',
      entityId: log.id,
      payload: {
        ...log,
        id: log.id,
        ...(options?.source ? { source: options.source } : {})
      }
    })

    return buildQueuedEntityMutationResult(log, {
      entityType: 'mortalityLog',
      entityId: log.id
    })
  },

  saveImportedLogs: async (logs: MortalityLog[]): Promise<MortalityLog[]> => {
    await ensureOfflineReady()
    if (logs.length === 0) return []

    const timestamp = nowIso()
    const normalizedLogs = logs.map((log) => normalizeMortalityLogRecord({
      candidate: log,
      createdAt: typeof log.createdAt === 'string' ? log.createdAt : timestamp,
      updatedAt: timestamp
    }))

    await dexieMortalityLogRepo.upsertMany(normalizedLogs)
    await enqueueEntityChanges(normalizedLogs.map((log) => ({
      entityType: 'mortalityLog' as const,
      op: 'upsert' as const,
      entityId: log.id,
      payload: {
        ...log,
        id: log.id
      }
    })))

    return normalizedLogs
  },

  update: async (id: string, patch: MortalityLogUpdatePayload): Promise<MortalityLogMutationResult> => {
    await ensureOfflineReady()

    const existing = await dexieMortalityLogRepo.getById(id)
    if (!existing) throw new Error('Mortality log not found')

    const next: MortalityLog = {
      ...existing,
      lot: Object.prototype.hasOwnProperty.call(patch, 'lot')
        ? toOptionalTrimmed(patch.lot ?? undefined)
        : existing.lot,
      lotId: Object.prototype.hasOwnProperty.call(patch, 'lotId')
        ? toOptionalTrimmed(patch.lotId ?? undefined)
        : existing.lotId,
      capturedByActorId: Object.prototype.hasOwnProperty.call(patch, 'capturedByActorId')
        ? toOptionalTrimmed(patch.capturedByActorId)
        : existing.capturedByActorId,
      recordedAt: patch.recordedAt ? normalizeRecordedAt(patch.recordedAt) : existing.recordedAt,
      mortalityCount: Object.prototype.hasOwnProperty.call(patch, 'mortalityCount')
        ? normalizeMortalityCount(patch.mortalityCount)
        : existing.mortalityCount,
      notes: Object.prototype.hasOwnProperty.call(patch, 'notes')
        ? toOptionalTrimmed(patch.notes ?? undefined)
        : existing.notes,
      metadata: Object.prototype.hasOwnProperty.call(patch, 'metadata')
        ? (patch.metadata ?? undefined)
        : existing.metadata,
      updatedAt: nowIso()
    }

    await dexieMortalityLogRepo.upsert(next)
    await enqueueEntityChange({
      entityType: 'mortalityLog',
      op: 'upsert',
      entityId: next.id,
      payload: {
        ...next,
        id: next.id
      }
    })

    return buildQueuedEntityMutationResult(next, {
      entityType: 'mortalityLog',
      entityId: next.id
    })
  },

  // Findings #66: every delete carries a reason; the server audits it
  // (CrewDataCorrection with the full prior row) when the outbox syncs.
  delete: async (id: string, audit: { reason: string; operatorActorId?: string }): Promise<MortalityLogDeleteResult> => {
    await ensureOfflineReady()

    const existing = await dexieMortalityLogRepo.getById(id)

    await dexieMortalityLogRepo.delete(id)
    await enqueueEntityChange({
      entityType: 'mortalityLog',
      op: 'delete',
      entityId: id,
      payload: existing ? { ...existing } : undefined,
      reason: audit.reason,
      operatorActorId: audit.operatorActorId
    })

    return buildQueuedEntityMutationResult(undefined, {
      entityType: 'mortalityLog',
      entityId: id
    })
  }
}